// Dragging a box type from the side panel onto the page. Once a drag starts a
// transparent shield covers the screen, so the viewer's own tools never see the
// gesture, and the drop point is mapped onto the page afterwards. No imports:
// the browser drag test loads this file as-is.

const DRAG_THRESHOLD_PX = 4;

const inside = (rect, x, y) => x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

// The page under a point on screen, in the viewer's page coordinates (top-left
// origin, what annotation X/Y use), or null when the point is not on a page.
export const pagePointAtClient = (instance, clientX, clientY) => {
  const documentViewer = instance?.Core?.documentViewer;
  const doc = documentViewer?.getDocument?.();
  const scrollView = documentViewer?.getScrollViewElement?.();
  if (!doc || !scrollView) return null;

  // WebViewer 11 renders in the page itself; older builds used an iframe,
  // whose events carry coordinates relative to the frame.
  const frameWindow = instance.UI?.iframeWindow;
  const frame = frameWindow?.frameElement;
  let x = clientX;
  let y = clientY;
  let EventType = window.MouseEvent;
  if (frame) {
    const frameRect = frame.getBoundingClientRect();
    if (!inside(frameRect, clientX, clientY)) return null;
    x -= frameRect.left;
    y -= frameRect.top;
    EventType = frameWindow.MouseEvent;
  }
  if (!inside(scrollView.getBoundingClientRect(), x, y)) return null;

  const point = documentViewer.getViewerCoordinatesFromMouseEvent(
    new EventType('mousemove', { clientX: x, clientY: y, bubbles: true })
  );
  const pageNumber = point?.pageNumber;
  if (!pageNumber || pageNumber < 1 || pageNumber > doc.getPageCount()) return null;
  // The point is in the page's unrotated frame; page info is as displayed.
  const info = doc.getPageInfo(pageNumber);
  const sideways = [90, 270].includes(doc.getPageRotation?.(pageNumber) || 0);
  const width = sideways ? info.height : info.width;
  const height = sideways ? info.width : info.height;
  if (point.x < 0 || point.y < 0 || point.x > width || point.y > height) return null;
  return { pageNumber, x: point.x, y: point.y };
};

// Call from a pointerdown. A press that barely moves is left to the button's
// own click; a real drag shows a ghost box (or the image, when given one) and
// reports where it was let go.
export const startBoxDrag = (downEvent, { label, color, onDrop, imageUrl = null, imageWidth = 150 }) => {
  if (downEvent.button !== 0) return;
  const startX = downEvent.clientX;
  const startY = downEvent.clientY;
  let shield = null;
  let ghost = null;

  // An image is dropped centred on the pointer, so it is shown there too.
  const place = (x, y) => {
    const offsetX = imageUrl ? -ghost.offsetWidth / 2 : 8;
    const offsetY = imageUrl ? -ghost.offsetHeight / 2 : 8;
    ghost.style.left = `${x + offsetX}px`;
    ghost.style.top = `${y + offsetY}px`;
  };

  const begin = () => {
    shield = document.createElement('div');
    shield.setAttribute('data-testid', 'esign-box-drag-shield');
    Object.assign(shield.style, {
      position: 'fixed', inset: '0', zIndex: '2147483000', cursor: 'grabbing', background: 'transparent',
    });
    ghost = document.createElement('div');
    ghost.setAttribute('data-testid', 'esign-box-drag-ghost');
    Object.assign(ghost.style, {
      position: 'fixed', zIndex: '2147483001', pointerEvents: 'none', padding: '6px 12px',
      border: `1px solid ${color}`, borderRadius: '4px', background: '#fff', color,
      fontSize: '12px', fontWeight: '600', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', whiteSpace: 'nowrap',
    });
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = label;
      Object.assign(image.style, { display: 'block', width: `${imageWidth}px`, height: 'auto' });
      Object.assign(ghost.style, { padding: '0', background: 'rgba(255,255,255,0.7)' });
      ghost.appendChild(image);
    } else {
      ghost.textContent = label;
    }
    document.body.appendChild(shield);
    document.body.appendChild(ghost);
  };

  const cleanup = () => {
    window.removeEventListener('pointermove', onMove, true);
    window.removeEventListener('pointerup', onUp, true);
    window.removeEventListener('pointercancel', cleanup, true);
    window.removeEventListener('blur', cleanup);
    window.removeEventListener('keydown', onKey, true);
    shield?.remove();
    ghost?.remove();
  };

  // Some browsers still send a click to the button after a drag; that click
  // must not also drop a box mid-page.
  const swallowNextClick = () => {
    const swallow = (event) => {
      event.preventDefault();
      event.stopPropagation();
      window.removeEventListener('click', swallow, true);
    };
    window.addEventListener('click', swallow, true);
    setTimeout(() => window.removeEventListener('click', swallow, true), 300);
  };

  function onMove(event) {
    if (!shield) {
      if (Math.hypot(event.clientX - startX, event.clientY - startY) < DRAG_THRESHOLD_PX) return;
      begin();
    }
    place(event.clientX, event.clientY);
  }

  // The viewer handles the release after us and would clear any selection we
  // make during it, so the drop is reported once the release has settled.
  function onUp(event) {
    if (!shield) {
      cleanup();
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    swallowNextClick();
    const { clientX, clientY } = event;
    window.removeEventListener('pointermove', onMove, true);
    window.removeEventListener('pointerup', onUp, true);
    setTimeout(() => {
      cleanup();
      onDrop(clientX, clientY);
    }, 0);
  }

  // Escape cancels the drag only; the placement window stays open.
  function onKey(event) {
    if (event.key !== 'Escape') return;
    if (shield) {
      event.preventDefault();
      event.stopPropagation();
    }
    cleanup();
  }

  window.addEventListener('pointermove', onMove, true);
  window.addEventListener('pointerup', onUp, true);
  window.addEventListener('pointercancel', cleanup, true);
  window.addEventListener('blur', cleanup);
  window.addEventListener('keydown', onKey, true);
};
